import { useInRouterContext, useNavigate } from 'react-router-dom';
import { RoleGuard } from '../../auth/RoleGuard';
import { useTaskControl } from '../../api/hooks';
import { ALERT_DEEP_LINK_EVENT, navigationTargetFor } from '../../state/alert-deep-link';
import type { RobotStateView, TaskState } from '../../api/rmf-client';
import type { LiftViewModel } from '../../api/adapters';
import {
  SITE_DONE_TEXT,
  SITE_VERDICT_STATUS_WORD,
  TASK_VERDICT_CODE_TEXT,
} from './task-command-verdict';
import { TASK_CONTROL_ROLES, taskControlOutcome, useTaskControlGates } from './task-control-gates';
import './TaskDetailDrawer.css';

/**
 * Control actions offered wherever a task can be acted on, each mapped to
 * the capability that gates it; the table and every rule that decides
 * what is offered live in `task-control-gates.ts`.
 */
export function TaskControlActions({
  task,
  robot: robotProp,
  lifts: liftsProp,
}: {
  task: TaskState;
  /** Supplied by the Robot drawer, which already has both. Omitted by
   *  the Task drawer, which looks them up below -- the same data, one
   *  query deduplicated by react-query either way. */
  robot?: RobotStateView;
  lifts?: readonly LiftViewModel[];
}) {
  const control = useTaskControl();

  // The action AND the task the outcome below belongs to. Both come
  // from the mutation's own `variables` rather than a second piece of
  // state, so the banner can never name a different verb — or sit
  // under a different task — from the one it is reporting on.
  // A verdict belongs to ONE task. This component is re-used IN PLACE
  // when the task under it changes: `RobotDetailDrawer` re-derives
  // `currentTask` live from `robot.taskId` (which moves on as soon as
  // the fleet hands the robot its next job) and neither drawer gives
  // this component a `key`, so the same `useMutation` state survives
  // the swap. Without this guard a cancel that landed on task A
  // renders as a green "Cancel: done" under task B — a false success
  // reading, which is the exact failure this whole module exists to
  // stop. The mutation state is deliberately NOT reset on the swap:
  // navigating back to task A should still show A's answer.
  //
  // A 409 is a verdict, not an error string: the api-server refuses a
  // control verb it knows cannot land (and, since the worker stopped
  // reporting unanswerable cancels as `accepted`, that now includes
  // `outcome_unknown`). `verdictFromError` is the lift drawer's reader
  // for exactly that envelope — reused rather than reimplemented, and
  // handed the same human label the success path uses so one banner
  // never says "Kill" on success and "kill" on refusal.
  //
  // But that envelope's `code` is `precondition_failed` for ALL THREE
  // of `rejected` / `timed_out` / `outcome_unknown` — the api-server
  // sends one code for the lot (`commands.py::_authorize_and_publish`)
  // — so read the CommandRecord it also ships in the body and let the
  // WORKER'S OWN STATE drive the banner. Without this the orphan case
  // rendered as "Cancel: refused (precondition_failed)" with no entry
  // in `TASK_VERDICT_CODE_TEXT`, and the one sentence that tells an
  // operator not to re-send a cancel nothing can ever answer — the
  // behaviour that stretched the 2026-08-28 incident to 45 h — was
  // written but never shown. The code alone is NOT enough to infer the
  // state: `refusedCommandFromError` returns null unless the record
  // itself says which of the three it was, so a genuine `rejected` is
  // never dressed up as an orphaning. When there is no record — an
  // older api-server, or any other 409 — this falls straight back to
  // the `verdictFromError` reading, which is still an honest refusal.
  const outcome = taskControlOutcome(control, task.id);

  const {
    availableActions,
    capabilityMap,
    capabilitiesLoaded,
    robot,
    inLift,
    fireHeldBanner,
    guardedForLift,
    heldTerminalOutsideLift,
    audience,
  } = useTaskControlGates(task, robotProp, liftsProp);

  // Keep rendering for the verdict alone: a cancel that lands moves the
  // task to a terminal state, which empties `availableActions` on the
  // very next render — returning null there would erase the answer the
  // operator pressed the button to get.
  if (availableActions.length === 0 && !outcome && !guardedForLift && !fireHeldBanner) return null;

  const robotName = robot?.name ?? task.assignedRobot ?? 'The robot';

  return (
    <RoleGuard allowedRoles={TASK_CONTROL_ROLES}>
      {guardedForLift && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--refused"
          data-testid="task-in-lift-pointer"
          role="alert"
        >
          <strong>Do not cancel in the car</strong>
          {audience === 'site' ? (
            // SPLIT BY AUDIENCE (plan W5 step 7; amendment A2). The push and
            // the Recover below are Nexsoss's procedure, not the hotel's.
            // `foh_operator` holds no lift verb at all, so Recover would be
            // a button it does not have. `site_supervisor` and
            // `service_operator` DO hold `lift.recover` (and the
            // supervisor `robot.decommission`); they read this line all
            // the same (`service_operator` only alongside a role the guard
            // above admits), because the split is by who the reader is,
            // not by which buttons the token carries. The attention card's
            // in-car branch makes the same call.
            <p>Don't cancel while it is in or at {inLift?.name}. Call Nexsoss.</p>
          ) : (
            <>
              <p>
                {robot?.name ?? task.assignedRobot} is in or boarding lift {inLift?.name}. Do not
                cancel in the car: hold the door, immobilise the robot, push it at least 1.2 m
                onto the landing, then press Recover for {inLift?.name} (Devices).
              </p>
              <p className="nx-task-drawer__sub">
                Decommissioning the robot with a Cancel policy is the audited way to end this task
                from here; it warns first and says what it did.
              </p>
            </>
          )}
        </div>
      )}
      {fireHeldBanner && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--refused"
          data-testid="task-fire-held"
          role="alert"
        >
          <strong>Held after fire alarm</strong>
          {audience === 'site' ? (
            // The hotel's words (batch B, Akash 2026-10-31): no vendor app,
            // which the hotel doesn't have. The steps live in one place,
            // the robot panel's Held after fire alarm section (the QR route,
            // D35), and this line sends the reader there.
            <p>
              Resume cannot release this task. {robotName} is waiting for someone to check which
              floor it is on. Open <RobotInFleetLink robotName={robotName} /> and follow the steps
              under Held after fire alarm.
            </p>
          ) : (
            <p>
              Resume cannot release this task. {robotName} is waiting for an operator to confirm
              which floor it is on. Localize it in the vendor app, then open{' '}
              <RobotInFleetLink robotName={robotName} /> and press Confirm floor and release.
            </p>
          )}
        </div>
      )}
      {heldTerminalOutsideLift && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--pending"
          data-testid="task-held-line"
          role="status"
        >
          {audience === 'site' ? (
            // No "RMF" and no robot-state age for the hotel (batch B,
            // 2026-10-31); what matters on the floor is that it may move.
            <p>
              {robotName} is still doing this job, although it is recorded as {task.status}. It
              may still move.
            </p>
          ) : (
            <p>
              RMF is still executing this task on {robot?.name ?? task.assignedRobot} (robot
              state{' '}
              {Math.max(0, Math.round((Date.now() - Date.parse(robot?.lastSeen ?? '')) / 1000))} s
              old), although it is recorded as {task.status}.
            </p>
          )}
        </div>
      )}
      <div className="nx-task-drawer__actions">
        {(guardedForLift ? [] : availableActions).map(({ action, label, capability, variant, hint }) => {
          const cap = capabilityMap.get(capability);
          const disabled = control.isPending || (capabilitiesLoaded && cap?.enabled === false);
          return (
            <button
              key={action}
              type="button"
              className={variant === 'danger' ? 'nx-task-drawer__cancel' : 'nx-task-drawer__action'}
              onClick={() => control.mutate({ taskId: task.id, action })}
              disabled={disabled}
              title={cap?.enabled === false ? cap.disabled_reasons.join(', ') : hint}
              data-testid={`task-${action}-btn`}
            >
              {control.isPending ? 'Sending…' : label}
            </button>
          );
        })}
      </div>
      {outcome?.kind === 'pending' && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--pending"
          data-testid="task-command-pending"
          data-state={control.data?.command.state ?? ''}
          role="status"
        >
          <strong>⏳ Not confirmed</strong>
          <p>{outcome.message}</p>
          {/* The api-server's own words ("published to task_api_requests",
              "zenoh queryable reply timeout") are ops' only (P8.8,
              2026-10-31); the sentence above is the hotel's. */}
          {audience === 'ops' && outcome.detail && (
            <p className="nx-task-drawer__sub">{outcome.detail}</p>
          )}
        </div>
      )}
      {outcome?.kind === 'verdict' && (
        <div
          className={`nx-task-drawer__banner nx-task-drawer__banner--${outcome.status}`}
          data-testid="task-command-verdict"
          data-status={outcome.status}
          data-code={outcome.code ?? ''}
          // A refusal or an unanswerable command must interrupt a
          // screen reader — it is the one thing that contradicts the
          // button the operator just pressed. Only "done" is
          // conversational.
          role={outcome.status === 'done' ? 'status' : 'alert'}
        >
          <strong>
            {audience === 'ops' ? (
              <>
                {outcome.verb}: {outcome.status}
                {outcome.code ? ` (${outcome.code})` : ''}
              </>
            ) : (
              `${outcome.verb}: ${SITE_VERDICT_STATUS_WORD[outcome.status] ?? outcome.status}`
            )}
          </strong>
          <p>
            {(outcome.code && TASK_VERDICT_CODE_TEXT[outcome.code]) ||
              (audience === 'site' && outcome.status === 'done' ? SITE_DONE_TEXT : outcome.reason)}
          </p>
          {audience === 'ops' &&
            outcome.code &&
            TASK_VERDICT_CODE_TEXT[outcome.code] &&
            outcome.reason && <p className="nx-task-drawer__sub">{outcome.reason}</p>}
        </div>
      )}
    </RoleGuard>
  );
}

/**
 * "{robot} in Fleet", as the same deep link an alert about the robot
 * would follow: the `nx:alert-deep-link` window event the Fleet screen
 * listens for, then a navigate to its path (`AlertNotificationsProvider::
 * handleAlertAction`). Both drawers that render this component sit
 * under the app router, but their component tests do not, so outside a
 * router this degrades to the plain words rather than throwing.
 */
function RobotInFleetLink({ robotName }: { robotName: string }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <RobotInFleetRouterLink robotName={robotName} />
  ) : (
    <span data-testid="task-fire-held-robot">{robotName} in Robots</span>
  );
}

function RobotInFleetRouterLink({ robotName }: { robotName: string }) {
  const navigate = useNavigate();
  const target = navigationTargetFor({ robotName });
  const open = () => {
    const dispatch = () =>
      window.dispatchEvent(new CustomEvent(ALERT_DEEP_LINK_EVENT, { detail: target.deepLink }));
    // Before the navigate, as the alert provider does -- that is what
    // opens the drawer when Fleet is already the mounted screen. And
    // once more after the route has committed: routes unmount on
    // navigation, so from the Tasks screen the Fleet listener does not
    // exist yet when the first event fires. The second is a no-op when
    // the first landed (selecting the selected robot again).
    dispatch();
    navigate(target.path);
    window.setTimeout(dispatch, 0);
  };
  return (
    <button
      type="button"
      className="nx-task-drawer__inline-link"
      onClick={open}
      data-testid="task-fire-held-robot"
    >
      {robotName} in Robots
    </button>
  );
}
